import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
// import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import Swal from "sweetalert2";

// Components, Icons
import TextAreaInput from "components/input/TextAreaInput";
import TextInput from "components/input/TextInput";
import RadioInput from "components/input/RadioInput";

// Apis
import useFetch from "utils/hooks/useFetch";
import {
  CREATE_TICKET_FEEDBACK,
  GET_USER_DETAIL_BY_TOKEN_EMAIL,
  ticket_api,
} from "api";

// Helpers
import useToastMessage from "helpers/toast-message";

function WebinarFeedbackForm() {
  const navigate = useNavigate();
  const { showError } = useToastMessage();
  // const { t } = useTranslation();
  const { id, participant_token } = useParams();

  const { fetch: createTicketFeedback } = useFetch(
    CREATE_TICKET_FEEDBACK,
    false,
    ticket_api,
  );

  const { fetch: getUserDetail } = useFetch(
    GET_USER_DETAIL_BY_TOKEN_EMAIL,
    false,
    ticket_api,
  );

  const [schedule, setSchedule] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: "",
      full_name: "",
      expectation: "",
      organized_well: "",
      presenter_masters_material: "",
      presenter_conveys_material: "",
      materials_matches: "",
      recommend: "",
      interested_in_other_program: "",
      interested_other_topic: "",
      interested_other_topic_other_text: "",
      comment_or_suggestion: "",
      testimonial: "",
      is_publish: "",
      ticket_booking_id: "",
    },
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .required("Please input your email address")
        .email("Invalid email format"),
      full_name: Yup.string().required("Please input your full name"),
      expectation: Yup.string().required("Please input webinar expectation "),
      organized_well: Yup.string().required("Please input this field"),
      presenter_masters_material: Yup.string().required(
        "Please input this field",
      ),
      presenter_conveys_material: Yup.string().required(
        "Please input this field",
      ),
      materials_matches: Yup.string().required("Please input this field"),
      recommend: Yup.string().required("Please input this field"),
      interested_in_other_program: Yup.string().required(
        "Please input this field",
      ),
      interested_other_topic: Yup.string().required("Please input this field"),
      interested_other_topic_other_text: Yup.string().when(
        "interested_other_topic",
        {
          is: (val) => val === "Other",
          then: (schema) => schema.required("Please input this field"),
          otherwise: (schema) => schema.notRequired(),
        },
      ),
      comment_or_suggestion: Yup.string().required("Please input this field"),
      testimonial: Yup.string().required("Please input this field"),
      is_publish: Yup.string().required("Please input this field"),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: (values) => {
      handleSubmitForm(values);
    },
  });

  const handleSubmitForm = (values) => {
    createTicketFeedback({
      data: {
        ...values,
        interested_other_topic:
          values.interested_other_topic === "Other"
            ? values.interested_other_topic_other_text
            : values.interested_other_topic,
        ticket_id: id,
      },
    })
      .then(() => {
        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          navigate(
            `/${ticket && ticket.webinar_type === "Webinar" ? "price" : "investment"}-details/${id}`,
          );
        }, 2000);
      })
      .catch((err) => {
        showError(err?.data?.message);
      });
  };

  const handleConfirmSubmitBtn = () => {
    Swal.fire({
      title: "Please recheck all your answers.",
      text: "Once submitted, you can't undo this.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Back to form",
      customClass: {
        confirmButton: "bg-orange-500 hover:bg-orange-400",
        cancelButton: "bg-slate-400",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        formik.handleSubmit();
      }
    });
  };

  useEffect(() => {
    if (participant_token && id) {
      getUserDetail({
        params: {
          participant_token,
        },
      })
        .then((res) => {
          if (res && res.participant_detail) {
            formik.setFieldValue("email", res.participant_detail.email);
            formik.setFieldValue("full_name", res.participant_detail.full_name);
            formik.setFieldValue(
              "ticket_booking_id",
              res.participant_detail.ticket_booking_id,
            );
            setTicket(res.participant_detail.booking.ticket);
            setSchedule(res.participant_detail.booking.schedule);
          } else {
            // redirect to home if invalid token
            navigate("/");
          }
        })
        .catch((err) => {
          showError(err?.data?.message);
        });
    }
  }, [id, participant_token]);

  return (
    <div>
      <section>
        <div className="max-w-xl px-4 py-4 mx-auto space-y-6 md:max-w-4xl lg:max-w-6xl sm:px-8 sm:py-8">
          <div className="text-center">
            <div className="space-y-4">
              <div className="app-animation-scale-start">
                <p className="text-2xl font-bold md:text-3xl xl:text-5xl">
                  Feedback Form
                </p>
              </div>
              <div className="pt-6 pb-6">
                <div className="pb-2 app-animation-scale-start">
                  <p className="text-xl font-bold capitalize md:text-2xl xl:text-4xl text-app-orange">
                    {ticket?.title}
                  </p>
                </div>
                <div className="app-animation-scale-start">
                  <p className="text-sm font-bold md:text-md xl:text-xl">
                    {schedule && schedule.date && schedule.start && schedule.end
                      ? `${moment(new Date(schedule.date)).format(
                          "DD MMMM YYYY",
                        )} ${schedule.start} - ${schedule.end}`
                      : ""}
                  </p>
                </div>
              </div>
              <div className="app-animation-scale-start">
                <p className="text-sm font-semibold uppercase md:text-sm xl:text-md">
                  Thank you for attending our webinar, please fill out this form
                  so we can learn how to improve our services.
                </p>
              </div>
              <div className="w-full px-4 md:px-6">
                <div className="max-w-2xl pt-4 mx-auto space-y-4 text-left">
                  <div className="app-animation-scale-start">
                    <TextInput
                      labelClassname={"text-app-dark-blue text-base md:text-lg"}
                      name="email"
                      label={"Email"}
                      required
                      placeholder={"Your answer..."}
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      error={formik.errors.email}
                      disabled
                    />
                  </div>
                  <div className="app-animation-scale-start">
                    <TextInput
                      labelClassname={"text-app-dark-blue text-base md:text-lg"}
                      name="full_name"
                      label={"Full Name"}
                      required
                      placeholder={"Your answer..."}
                      value={formik.values.full_name}
                      onChange={formik.handleChange}
                      error={formik.errors.full_name}
                      disabled
                    />
                  </div>
                  <div className="flex flex-col space-y-2 app-animation-scale-start">
                    <label className="text-sm font-bold sm:text-base">
                      <p className="text-base text-app-dark-blue md:text-lg">
                        Did the webinar meet your expectations?{" "}
                        <span className="text-red-500">*</span>
                      </p>
                      <p className="text-sm font-semibold">
                        1 = Not at all, 5 = It was great
                      </p>
                    </label>
                    <div className="p-6 border rounded-lg">
                      <div className="flex items-center justify-between space-x-4">
                        <span className="text-sm">Not at all</span>
                        <RadioInput
                          name="expectation"
                          options={[
                            { id: "1", name: "expectation-1", text: "1" },
                            { id: "2", name: "expectation-2", text: "2" },
                            { id: "3", name: "expectation-3", text: "3" },
                            { id: "4", name: "expectation-4", text: "4" },
                            { id: "5", name: "expectation-5", text: "5" },
                          ]}
                          value={formik.values.expectation}
                          styled={false}
                          onChange={formik.handleChange}
                          className="flex justify-between w-full max-w-md space-x-6"
                          error={formik.errors.expectation}
                          customFlexCol={true}
                        />
                        <span className="text-sm">It was great</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col space-y-2 app-animation-scale-start">
                    <label className="text-sm font-bold sm:text-base">
                      <p className="text-base text-app-dark-blue md:text-lg">
                        Do you think the committee organized this webinar well
                        enough? <span className="text-red-500">*</span>
                      </p>
                      <p className="text-sm font-semibold">
                        1 = Not at all, 5 = It was great
                      </p>
                    </label>
                    <div className="p-6 border rounded-lg">
                      <div className="flex items-center justify-between space-x-4">
                        <span className="text-sm">Not at all</span>
                        <RadioInput
                          name="organized_well"
                          options={[
                            { id: "1", name: "organized_well-1", text: "1" },
                            { id: "2", name: "organized_well-2", text: "2" },
                            { id: "3", name: "organized_well-3", text: "3" },
                            { id: "4", name: "organized_well-4", text: "4" },
                            { id: "5", name: "organized_well-5", text: "5" },
                          ]}
                          value={formik.values.organized_well}
                          styled={false}
                          onChange={formik.handleChange}
                          className="flex justify-between w-full max-w-md space-x-6"
                          error={formik.errors.organized_well}
                          customFlexCol={true}
                        />
                        <span className="text-sm">It was great</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col space-y-2">
                    <label className="text-sm font-bold sm:text-base">
                      <p className="text-base text-app-dark-blue md:text-lg app-animation-scale-start">
                        How well do you think the presenter delivers this
                        webinar? <span className="text-red-500">*</span>
                      </p>
                      <p className="text-sm font-semibold">
                        1 = Very poor, 5 = Excellent
                      </p>
                    </label>

                    <div className="p-6 border rounded-lg app-animation-scale-start">
                      <p className="pb-4 font-semibold text-md">
                        The presenter masters the material given
                      </p>
                      <div className="flex items-center justify-between space-x-4">
                        <span className="text-sm">Very poor</span>
                        <RadioInput
                          name="presenter_masters_material"
                          options={[
                            {
                              id: "1",
                              name: "presenter_masters_material-1",
                              text: "1",
                            },
                            {
                              id: "2",
                              name: "presenter_masters_material-2",
                              text: "2",
                            },
                            {
                              id: "3",
                              name: "presenter_masters_material-3",
                              text: "3",
                            },
                            {
                              id: "4",
                              name: "presenter_masters_material-4",
                              text: "4",
                            },
                            {
                              id: "5",
                              name: "presenter_masters_material-5",
                              text: "5",
                            },
                          ]}
                          value={formik.values.presenter_masters_material}
                          styled={false}
                          onChange={formik.handleChange}
                          className="flex justify-between w-full max-w-md space-x-6"
                          error={formik.errors.presenter_masters_material}
                          customFlexCol={true}
                        />
                        <span className="text-sm">Excellent</span>
                      </div>
                    </div>

                    <div className="p-6 border rounded-lg app-animation-scale-start">
                      <p className="pb-4 font-semibold text-md">
                        The presenter conveys the material clearly
                      </p>
                      <div className="flex items-center justify-between space-x-4">
                        <span className="text-sm">Very poor</span>
                        <RadioInput
                          name="presenter_conveys_material"
                          options={[
                            {
                              id: "1",
                              name: "presenter_conveys_material-1",
                              text: "1",
                            },
                            {
                              id: "2",
                              name: "presenter_conveys_material-2",
                              text: "2",
                            },
                            {
                              id: "3",
                              name: "presenter_conveys_material-3",
                              text: "3",
                            },
                            {
                              id: "4",
                              name: "presenter_conveys_material-4",
                              text: "4",
                            },
                            {
                              id: "5",
                              name: "presenter_conveys_material-5",
                              text: "5",
                            },
                          ]}
                          value={formik.values.presenter_conveys_material}
                          styled={false}
                          onChange={formik.handleChange}
                          className="flex justify-between w-full max-w-md space-x-6"
                          error={formik.errors.presenter_conveys_material}
                          customFlexCol={true}
                        />
                        <span className="text-sm">Excellent</span>
                      </div>
                    </div>

                    <div className="p-6 border rounded-lg app-animation-scale-start">
                      <p className="pb-4 font-semibold text-md">
                        The material delivered matches your conditions and needs
                      </p>
                      <div className="flex items-center justify-between space-x-4">
                        <span className="text-sm">Very poor</span>
                        <RadioInput
                          name="materials_matches"
                          options={[
                            { id: "1", name: "materials_matches-1", text: "1" },
                            { id: "2", name: "materials_matches-2", text: "2" },
                            { id: "3", name: "materials_matches-3", text: "3" },
                            { id: "4", name: "materials_matches-4", text: "4" },
                            { id: "5", name: "materials_matches-5", text: "5" },
                          ]}
                          value={formik.values.materials_matches}
                          styled={false}
                          onChange={formik.handleChange}
                          className="flex justify-between w-full max-w-md space-x-6"
                          error={formik.errors.materials_matches}
                          customFlexCol={true}
                        />
                        <span className="text-sm">Excellent</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 border rounded-lg app-animation-scale-start">
                    <RadioInput
                      label={
                        <p className="text-base text-left text-app-dark-blue md:text-lg">
                          Would you recommend this webinar to your colleagues?
                          <span className="text-red-500">*</span>
                        </p>
                      }
                      options={[
                        { id: "Yes", name: "recommend-1", text: "Yes" },
                        {
                          id: "Possibly",
                          name: "recommend-2",
                          text: "Possibly",
                        },
                        { id: "No", name: "recommend-3", text: "No" },
                      ]}
                      className={
                        "flex flex-col items-start space-x-0 space-y-2"
                      }
                      customClassName={"space-x-5"}
                      name="recommend"
                      onChange={formik.handleChange}
                      value={formik.values.recommend}
                      error={formik.errors.recommend}
                    />
                  </div>

                  <div className="p-6 border rounded-lg app-animation-scale-start">
                    <RadioInput
                      label={
                        <p className="text-base text-left text-app-dark-blue md:text-lg">
                          Are you interested in participating in other ISA
                          webinar/workshop in the future?
                          <span className="text-red-500">*</span>
                        </p>
                      }
                      options={[
                        {
                          id: "Yes",
                          name: "interested_in_other_program-1",
                          text: "Yes",
                        },
                        {
                          id: "Possibly",
                          name: "interested_in_other_program-2",
                          text: "Possibly",
                        },
                        {
                          id: "No",
                          name: "interested_in_other_program-3",
                          text: "No",
                        },
                      ]}
                      className={
                        "flex flex-col items-start space-x-0 space-y-2"
                      }
                      customClassName={"space-x-5"}
                      name="interested_in_other_program"
                      onChange={formik.handleChange}
                      value={formik.values.interested_in_other_program}
                      error={formik.errors.interested_in_other_program}
                    />
                  </div>

                  <div className="p-6 border rounded-lg app-animation-scale-start">
                    <RadioInput
                      label={
                        <>
                          <p className="text-base text-left text-app-dark-blue md:text-lg">
                            What other topics are you interested in learning
                            more about? in other ISA webinar/workshop in the
                            future?
                            <span className="text-red-500">*</span>
                          </p>
                          <p className="text-sm text-left">
                            If there are other topics, please fill in the
                            "Other/Lainnya" option.
                          </p>
                        </>
                      }
                      options={[
                        {
                          id: "Measuring Social Return On Investment (SROI)",
                          name: "expectation-1",
                          text: "Measuring Social Return On Investment (SROI)",
                        },
                        {
                          id: "CSV (Creating Shared Value)",
                          name: "expectation-2",
                          text: "CSV (Creating Shared Value)",
                        },
                        {
                          id: "Community Development that Creates Sustainable Impact",
                          name: "expectation-3",
                          text: "Community Development that Creates Sustainable Impact",
                        },
                        {
                          id: "Sustainable Business Model Plan",
                          name: "expectation-1",
                          text: "Sustainable Business Model Plan",
                        },
                        { id: "Other", name: "expectation-2", text: "Other" },
                      ]}
                      className={
                        "flex flex-col items-start space-x-0 space-y-2"
                      }
                      customClassName={"space-x-5"}
                      name="interested_other_topic"
                      onChange={formik.handleChange}
                      value={formik.values.interested_other_topic}
                    />
                    {formik.values.interested_other_topic === "Other" && (
                      <input
                        type="text"
                        name="interested_other_topic_other_text"
                        value={formik.values.interested_other_topic_other_text}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Please specify other topic"
                        className="w-full max-w-md px-1 mt-2 text-sm border-b-2 border-orange-500 focus:outline-none"
                      />
                    )}

                    {((!formik.values.interested_other_topic &&
                      formik.errors.interested_other_topic) ||
                      (formik.values.interested_other_topic === "Other" &&
                        formik.errors.interested_other_topic_other_text &&
                        formik.touched.interested_other_topic_other_text)) && (
                      <p className="mt-2 text-xs text-red-500">
                        {formik.errors.interested_other_topic ||
                          formik.errors.interested_other_topic_other_text}
                      </p>
                    )}
                  </div>

                  <div className="app-animation-scale-start">
                    <TextAreaInput
                      labelClassname={
                        "text-app-dark-blue text-base md:text-lg text-left"
                      }
                      name="comment_or_suggestion"
                      label={
                        "Do you have any other comments or suggestions regarding the session or the overall webinar?"
                      }
                      required
                      placeholder={"Your answer..."}
                      rows={8}
                      value={formik.values.comment_or_suggestion}
                      onChange={formik.handleChange}
                      error={formik.errors.comment_or_suggestion}
                    />
                  </div>
                  <div className="app-animation-scale-start">
                    <TextAreaInput
                      labelClassname={"text-app-dark-blue text-base md:text-lg"}
                      name="testimonial"
                      label={"Please provide your testimonial for this webinar"}
                      required
                      placeholder={"Your answer..."}
                      rows={8}
                      value={formik.values.testimonial}
                      onChange={formik.handleChange}
                      error={formik.errors.testimonial}
                    />
                  </div>

                  <div className="p-6 border rounded-lg app-animation-scale-start">
                    <RadioInput
                      label={
                        <p className="text-base text-left text-app-dark-blue md:text-lg">
                          Are you willing to have your testimonial displayed on
                          the ISA website?
                          <span className="text-red-500">*</span>
                        </p>
                      }
                      options={[
                        { id: "Yes", name: "is_publish-1", text: "Yes" },
                        { id: "No", name: "is_publish-2", text: "No" },
                      ]}
                      className={
                        "flex flex-col items-start space-x-0 space-y-2"
                      }
                      customClassName={"space-x-5"}
                      name="is_publish"
                      onChange={formik.handleChange}
                      value={formik.values.is_publish}
                      error={formik.errors.is_publish}
                    />
                  </div>

                  <div className="app-animation-scale-start">
                    <button
                      onClick={() => handleConfirmSubmitBtn()}
                      className="w-full px-12 py-2 text-base font-semibold text-white uppercase bg-black rounded-md md:text-lg disabled:opacity-50"
                      disabled={
                        !formik.values.email ||
                        formik.values.email === "" ||
                        !formik.values.full_name ||
                        formik.values.full_name === "" ||
                        !formik.values.expectation ||
                        formik.values.expectation === "" ||
                        !formik.values.organized_well ||
                        formik.values.organized_well === "" ||
                        !formik.values.presenter_masters_material ||
                        formik.values.presenter_masters_material === "" ||
                        !formik.values.presenter_conveys_material ||
                        formik.values.presenter_conveys_material === "" ||
                        !formik.values.materials_matches ||
                        formik.values.materials_matches === "" ||
                        !formik.values.recommend ||
                        formik.values.recommend === "" ||
                        !formik.values.interested_in_other_program ||
                        formik.values.interested_in_other_program === "" ||
                        !formik.values.interested_other_topic ||
                        formik.values.interested_other_topic === "" ||
                        !formik.values.comment_or_suggestion ||
                        formik.values.comment_or_suggestion === "" ||
                        !formik.values.testimonial ||
                        formik.values.testimonial === "" ||
                        formik.values.is_publish === ""
                      }
                    >
                      Submit
                    </button>
                  </div>
                  {isSubmitted && (
                    <p className="text-xs text-center text-app-light-blue md:text-sm">
                      Your feedback has successfully been submitted
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default WebinarFeedbackForm;
